import React, {useEffect, useState, useRef, useContext} from 'react'
import {
  Container,
  Button,
  Grid,
  Typography,
  Box,
  Card,
  TextField,
  MenuItem,
} from '@material-ui/core'
import WizardPreview from '../BarcodeWizardPreview'
import TooltipSettings from '../BarcodeToolTip'
import {automaticTooltip} from '../BarcodeToolTip/toolTipInfo'
import {createBarcodeSetting} from '../../graphql/barcode/mutations/barcodeSettingMutation'
import {Mutation} from 'react-apollo'
import BarcodeNavHeaderButtons from '../BarcodeNavHeaderButtons'
import {libraryPageQuery} from '../../graphql/barcode/queries/libraryPageQuery'
import LoadingScreen from '../BarcodeLoadingScreen'
import {useTranslation} from 'react-i18next'
import useStyles from './BarcodeWizardAutoForm.styles'
import {BarcodeUserSettingsContext} from '../../context/BarcodeUserSettings'
import {BarcodeLabelRecordContext} from '../../context/BarcodeLabelRecord'
import {BarcodeWizardContext} from '../../context/BarcodeWizard'
import {ToastMessageContext} from '../../context/ToastMessage'
import {BarcodeUrlSkipContext} from '../../context/BarcodeUrlSkip'
import {UserContext} from '../../context/UserContext'
import {validateBarcodePermission} from '../../modules/common/utils/permissions'

export default function BarcodeWizardAutoForm(props) {
  const classes = useStyles()
  const {t} = useTranslation()
  const {barcodeLabelRecord} = useContext(BarcodeLabelRecordContext)
  const {userSettings} = useContext(BarcodeUserSettingsContext)
  const {autoGenerateFormValues, setAutoGenerateFormValues} = useContext(BarcodeWizardContext)
  const [formValues, setFormValues] = React.useState(
    autoGenerateFormValues || {
      name: '',
      output: '',
      template: barcodeLabelRecord ? barcodeLabelRecord : {},
      digits: '3',
      prefix: '',
      startNumber: '1',
    }
  )
  const [nameError, setNameError] = useState(false)
  const [outputError, setOutputError] = useState(false)
  const [digitsError, setDigitsError] = useState(false)
  const [redirectTo, setRedirectTo] = useState('library')
  const {urlGoTo} = useContext(BarcodeUrlSkipContext)
  const accessToken = JSON.parse(window.localStorage.getItem('token'))
  const userId = parseInt(window.localStorage.getItem('user_id'))
  const toast = useContext(ToastMessageContext)
  const refBtnSave = useRef(null)
  const currentUser = React.useContext(UserContext)

  useEffect(() => {
    if (window !== window.parent) {
      window.parent.postMessage('scrollTop', process.env.REACT_APP_BARCODE_WINDOW_URL)
    } else {
      window.scrollTo(0, 0)
    }
  }, [])

  const handleChange = textField => event => {
    setFormValues({
      ...formValues,
      [textField]: event.target.value,
    })
    setAutoGenerateFormValues(formValues)
  }

  function handleSave(barcodeSetting, event) {
    if(!validateBarcodePermission(currentUser, "can_add"))
    {
      return;
    }

    if (checkValidity()) {
      barcodeSetting({
        variables: {
          accountId: userSettings.accountId,
          barcodeLabelId: parseInt(formValues.template.id),
          output: formValues.output,
          digits: parseInt(formValues.digits),
          prefix: formValues.prefix,
          startNumber: parseFloat(formValues.startNumber),
          name: formValues.name,
          dataSource: 'automated',
        },
      })
      setAutoGenerateFormValues(formValues)
      setRedirectTo(event.currentTarget.value)
    }
  }

  function checkReturnData(data) {
    if (data.createBarcodeSetting.errors.length > 0) {
      toast.popMessage(data.createBarcodeSetting.errors, 'error', refBtnSave.current)
    } else {
      if (redirectTo === 'library') {
        props.history.push({
          pathname: 'barcode-library',
        })
      } else {
        props.history.push({
          pathname: 'barcode-wizard-generate-automatic',
          state: {
            barcodeData: data.createBarcodeSetting.barcodeSettings,
            previous: 'barcode-wizard-auto-form',
          },
        })
      }
    }
  }

  function checkValidity() {
    const digit = parseInt(formValues.digits)
    let errorMessage = ''
    setDigitsError(false)
    setOutputError(false)
    setNameError(false)
    if (formValues.name === '') {
      errorMessage = t('barcode.wizard.automatic.error-name')
      setNameError(true)
    } else if (formValues.output === '') {
      errorMessage = t('barcode.wizard.automatic.error-output')
      setOutputError(true)
    } else if (digit <= 0 || digit > 50) {
      errorMessage = t('barcode.wizard.automatic.error-digits')
      setDigitsError(true)
    } else if (formValues.startNumber.length > digit) {
      errorMessage = t('barcode.wizard.automatic.error-digits-start-number')
      setDigitsError(true)
    } else if (parseInt(formValues.startNumber) < 0) {
      errorMessage = t('barcode.wizard.automatic.error-start-number')
    }

    if (errorMessage === '') {
      return true
    } else {
      toast.popMessage(errorMessage, 'error', refBtnSave.current)
      return false
    }
  }

  return (
    <div>
      <BarcodeNavHeaderButtons
        previous={urlGoTo ? 'barcode-wizard' : 'barcode-wizard-generate-setting'}
      />

      <Grid container>
        <Grid item xs={6}>
          <Container>
            <Grid container>
              <Grid container item xs={10} justify="flex-start">
                <Typography variant="h5">
                  <Box marginTop={3}>{t('barcode.wizard.automatic.automatic-generate')}</Box>
                </Typography>
              </Grid>
              <Grid container item xs={2} justify="flex-end" className={classes.infoTooltipMargin}>
                <TooltipSettings size={16} tooltipInfo={automaticTooltip} />
              </Grid>
            </Grid>
          </Container>
          <Box marginTop={4} />
          <Container>
            <Grid container spacing={5}>
              <Grid container item xs={8} justify="flex-end">
                <TextField
                  id="outlined-name"
                  label={t('barcode.wizard.automatic.name')}
                  value={formValues.name}
                  fullWidth
                  variant="outlined"
                  onChange={handleChange('name')}
                  error={nameError}
                />
              </Grid>

              <Grid container item xs={4} justify="flex-start">
                <TextField
                  id="outlined-select-output"
                  select
                  label={t('barcode.wizard.automatic.output')}
                  value={formValues.output}
                  fullWidth={true}
                  onChange={handleChange('output')}
                  error={outputError}
                  SelectProps={{
                    MenuProps: {
                      getContentAnchorEl: null,
                      anchorOrigin: {
                        vertical: 'bottom',
                        horizontal: 'left',
                      },
                    },
                  }}
                  variant="outlined"
                >
                  {['PDF', 'PNG'].map(output => (
                    <MenuItem key={output} value={output}>
                      {output}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>
            <Box marginTop={3} />
            <Grid container spacing={5}>
              <Grid container item xs={8} justify="flex-end">
                <TextField
                  id="outlined-select-template"
                  select
                  label={t('barcode.wizard.automatic.label-template')}
                  value={formValues.template}
                  fullWidth={true}
                  onChange={handleChange('template')}
                  SelectProps={{
                    MenuProps: {
                      getContentAnchorEl: null,
                      anchorOrigin: {
                        vertical: 'bottom',
                        horizontal: 'left',
                      },
                    },
                  }}
                  variant="outlined"
                  disabled
                >
                  <MenuItem value={formValues.template}>{formValues.template.name}</MenuItem>
                </TextField>
              </Grid>

              <Grid container item xs={4} justify="flex-start">
                <TextField
                  id="outlined-select-digits"
                  label={t('barcode.wizard.automatic.digits')}
                  error={digitsError}
                  type="number"
                  value={formValues.digits}
                  fullWidth={true}
                  onChange={handleChange('digits')}
                  variant="outlined"
                />
              </Grid>
            </Grid>
            <Box marginTop={3} />
            <Grid container spacing={5}>
              <Grid container item xs={8} justify="flex-end">
                <TextField
                  id="outlined-select-prefix"
                  label={t('barcode.wizard.automatic.prefix')}
                  value={formValues.prefix}
                  fullWidth={true}
                  onChange={handleChange('prefix')}
                  variant="outlined"
                />
              </Grid>

              <Grid container item xs={4} justify="flex-start">
                <TextField
                  id="outlined-select-number"
                  label={t('barcode.wizard.automatic.starting-number')}
                  type="number"
                  value={formValues.startNumber}
                  fullWidth={true}
                  onChange={handleChange('startNumber')}
                  variant="outlined"
                />
                {formValues.startNumber && parseInt(formValues.startNumber.charAt(0)) === 0 && (
                  <span style={{fontSize: '10px'}}>
                    {t('barcode.wizard.automatic.start-number-details')}
                  </span>
                )}
              </Grid>
            </Grid>
            <Box marginTop={3} />
            <Card className={classes.infoBackground}>
              <Box marginTop={2} marginBottom={2} marginLeft={2}>
                {t('barcode.wizard.automatic.last-number-details')}
              </Box>
            </Card>
            <Box marginTop={3} />
            <Grid container spacing={5} justify="flex-end">
              <Mutation
                mutation={createBarcodeSetting}
                refetchQueries={() => [
                  {
                    query: libraryPageQuery,
                    variables: {
                      accountId: userSettings.accountId,
                      token: accessToken,
                      userId: userId,
                      filter: '',
                    },
                  },
                ]}
                awaitRefetchQueries
                onCompleted={data => checkReturnData(data)}
              >
                {(createBarcodeSetting, {data, loading}) => {
                  if (loading)
                    return (
                      <div>
                        <LoadingScreen size={50} />
                      </div>
                    )
                  return (
                    <div>
                      <Button
                        ref={refBtnSave}
                        value="library"
                        variant="outlined"
                        className={classes.buttonLink}
                        disabled = {!validateBarcodePermission(currentUser, "can_add")}
                        color="primary"
                        onClick={e => handleSave(createBarcodeSetting, e)}
                      >
                        {t('barcode.wizard.automatic.save-library')}
                      </Button>

                      <Button
                        value="generate"
                        variant="contained"
                        className={classes.buttonLink}
                        disabled = {!validateBarcodePermission(currentUser, "can_add")}
                        color="primary"
                        onClick={e => handleSave(createBarcodeSetting, e)}
                      >
                        {t('barcode.wizard.automatic.save-generate')}
                      </Button>
                    </div>
                  )
                }}
              </Mutation>
            </Grid>
          </Container>
        </Grid>

        <Grid container item xs={6}>
          <WizardPreview />
        </Grid>
      </Grid>
    </div>
  )
}
